import { useState, useEffect } from 'react';
import { api } from '../lib/api.ts';
import { EmailPreview } from '../components/EmailPreview.tsx';
import { ScoreBadge } from '../components/ScoreBadge.tsx';
import { ClassificationBadge } from '../components/ClassificationBadge.tsx';
import type { DigestPreview as DigestPreviewData } from '../lib/types.ts';

function fmtTs(ts: number | undefined): string {
  if (!ts) return '—';
  return new Date(ts * 1000).toLocaleString('en-IE');
}

export function DigestPreview() {
  const [data, setData] = useState<DigestPreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState('');
  const [tab, setTab] = useState<'items' | 'html'>('items');

  async function load() {
    setLoading(true);
    try {
      setData(await api.digest.preview());
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleSend() {
    if (!confirm('Trigger digest re-send?')) return;
    setSending(true);
    try {
      const res = await api.digest.send();
      setSendMsg(res.message);
      await load();
    } finally {
      setSending(false);
    }
  }

  if (loading) return <p style={{ color: '#999' }}>Loading digest preview…</p>;
  if (!data) return null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 20 }}>Digest Preview</h1>
        <span style={{ fontSize: 13, color: '#666' }}>{data.date}</span>
        {data.already_sent ? (
          <span style={{ fontSize: 12, background: '#e8f5e9', color: '#27ae60', padding: '2px 8px', borderRadius: 3 }}>
            Sent at {fmtTs(data.sent_at)}
          </span>
        ) : (
          <span style={{ fontSize: 12, background: '#fff3e0', color: '#e67e22', padding: '2px 8px', borderRadius: 3 }}>
            Pending — {data.pending_count} items queued
          </span>
        )}
        <button onClick={handleSend} disabled={sending} style={{ marginLeft: 'auto', fontSize: 12, padding: '4px 12px', background: '#d94f00', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
          {sending ? 'Working…' : 'Reset & resend'}
        </button>
      </div>

      {sendMsg && <p style={{ color: '#27ae60', fontSize: 13 }}>{sendMsg}</p>}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {(['items', 'html'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ fontSize: 12, padding: '4px 12px', background: tab === t ? '#111' : '#f0f0f0', color: tab === t ? '#fff' : '#555', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
            {t === 'items' ? `Items (${data.pending_items.length})` : 'Email HTML'}
          </button>
        ))}
      </div>

      {tab === 'items' && (
        <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 6, overflow: 'hidden' }}>
          {data.pending_items.length === 0 ? (
            <p style={{ padding: 20, color: '#999', fontStyle: 'italic' }}>No items pending for today's digest.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 600, fontSize: 11 }}>Source</th>
                  <th style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 600, fontSize: 11 }}>Title</th>
                  <th style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 600, fontSize: 11 }}>Score</th>
                  <th style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 600, fontSize: 11 }}>Type</th>
                </tr>
              </thead>
              <tbody>
                {data.pending_items.map(item => (
                  <tr key={item.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '8px 14px' }}>{item.source_name}</td>
                    <td style={{ padding: '8px 14px', maxWidth: 320 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.title ?? '—'}
                      </div>
                    </td>
                    <td style={{ padding: '8px 14px' }}><ScoreBadge score={item.deep_final_score} /></td>
                    <td style={{ padding: '8px 14px' }}><ClassificationBadge classification={item.deep_final_classification ?? null} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'html' && (
        <EmailPreview html={data.email_html} subject={data.subject} />
      )}
    </div>
  );
}
